import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PackageCheck,
  PackagePlus,
  FileOutput,
  ArrowRightLeft,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';

const InventaireDocumentCreation = () => {
  const { t } = useTranslation();
  const p = (key) => t(`modules.inventaire.pages.inventaireDocumentCreation.${key}`);
  const navigate = useNavigate();
  const { isDashboardLightMode } = useGlobalTheme();
  const documentCreationLightClass = isDashboardLightMode ? 'document-creation-light' : '';

  const inventaireDocuments = [
    {
      id: 'reception-note',
      titleKey: 'doc_reception_title',
      subtitleKey: 'doc_reception_subtitle',
      descKey: 'doc_reception_desc',
      icon: PackageCheck,
      color: '#f97316',
    },
    {
      id: 'stock-entry',
      titleKey: 'doc_entry_title',
      subtitleKey: 'doc_entry_subtitle',
      descKey: 'doc_entry_desc',
      icon: PackagePlus,
      color: '#f97316',
    },
    {
      id: 'exit-note',
      titleKey: 'doc_exit_title',
      subtitleKey: 'doc_exit_subtitle',
      descKey: 'doc_exit_desc',
      icon: FileOutput,
      color: '#f97316',
    },
    {
      id: 'transfer-note',
      titleKey: 'doc_transfer_title',
      subtitleKey: 'doc_transfer_subtitle',
      descKey: 'doc_transfer_desc',
      icon: ArrowRightLeft,
      color: '#f97316',
    },
  ];

  const handleDocumentSelect = (doc) => {
    navigate(`/document-creation?type=${encodeURIComponent(doc.id)}&from=inventaire`);
  };

  return (
    <div
      className={documentCreationLightClass}
      style={{
        minHeight: '100vh',
        padding: '1.5rem',
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
      }}
    >
      <div className="mb-3">
        <h1
          className={`text-3xl font-bold mb-1 flex items-center gap-3 ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}
        >
          <FileText className="h-8 w-8 text-blue-400" />
          {t('sidebar.create_document')}
        </h1>
        <p className={`text-sm mb-2 ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
          {p('subtitle')}
        </p>
      </div>

      <div className="w-full">
        <motion.div
          className="document-categories"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="relative"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {inventaireDocuments.map((doc, docIndex) => (
                <motion.div
                  key={doc.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: docIndex * 0.05 }}
                  onClick={() => handleDocumentSelect(doc)}
                  className="group relative cursor-pointer"
                >
                  <div className="relative h-full p-5 rounded-2xl border transition-all duration-300 bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-white/30 hover:shadow-2xl hover:shadow-black/20 hover:-translate-y-2">
                    <div
                      className="absolute top-0 left-5 right-5 h-1 rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background: `linear-gradient(90deg, transparent, ${doc.color}, transparent)`,
                      }}
                    />

                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="p-3 rounded-xl transition-all duration-300 group-hover:scale-110"
                        style={{
                          background: `linear-gradient(135deg, ${doc.color}20, ${doc.color}10)`,
                          border: `1px solid ${doc.color}30`,
                        }}
                      >
                        {React.createElement(doc.icon, {
                          size: 22,
                          style: { color: doc.color },
                          className: 'transition-transform duration-300 group-hover:scale-110',
                        })}
                      </div>
                      <ArrowRight className="h-5 w-5 text-white/20 group-hover:text-white/60 group-hover:translate-x-1 transition-all duration-300" />
                    </div>

                    <div>
                      <h3 className="font-semibold text-white text-base mb-1 group-hover:text-white transition-colors line-clamp-2">
                        {p(doc.titleKey)}
                      </h3>
                      <p className="text-xs text-white/40 mb-2 uppercase tracking-wider font-medium">
                        {p(doc.subtitleKey)}
                      </p>
                      <p className="text-sm text-white/60 line-clamp-2 leading-relaxed">
                        {p(doc.descKey)}
                      </p>
                    </div>

                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{
                        background: `radial-gradient(circle at 50% 0%, ${doc.color}15 0%, transparent 70%)`,
                      }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default InventaireDocumentCreation;
